"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Mail, Phone, MapPin, Clock, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactForm() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: detailsRef,
    isVisible: detailsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section ref={sectionRef} className="py-24 bg-background">
            <div className="container mx-auto px-6 max-w-7xl">
                <div className="grid md:grid-cols-2 gap-12 items-start">
                    {}
                    <div ref={formRef} className={`transition-all duration-[150ms] ease-out ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2.5'}`}>
                        <h2 className="text-3xl font-display font-bold text-foreground mb-2">
                            Get In Touch
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Fill out the form below and we'll get back to you as soon as possible.
                        </p>

                        <FormProvider className="space-y-6" formId="contact-form-main" sectionName="contact-form-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 500
            }
          }} submitText="Send Message" successMessage="Thank you for contacting K&M Mechanical Services! We'll respond within 24 hours." errorMessage="Failed to submit the form. Please try again or call us directly.">
                            <ContactFormInner />
                        </FormProvider>
                    </div>

                    {}
                    <div ref={detailsRef} className={`space-y-6 transition-all duration-[150ms] ease-out delay-[150ms] ${detailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2.5'}`}>
                        <h2 className="text-3xl font-display font-bold text-foreground mb-8">
                            Contact Information
                        </h2>

                        <div className="space-y-6">
                            {}
                            <div className="flex items-start gap-4 group">
                                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/10 flex items-center justify-center transition-all duration-[120ms] ease-in-out group-hover:-translate-y-0.5">
                                    <Phone className="size-5 text-primary" />
                                </div>
                                <div>
                                    
                                    <Link to="tel:+19412980953" className="text-muted-foreground hover:text-primary transition-colors">(412) 980-0953</Link>
                                </div>
                            </div>

                            {}
                            <div className="flex items-start gap-4 group">
                                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/10 flex items-center justify-center transition-all duration-[120ms] ease-in-out group-hover:-translate-y-0.5">
                                    <Mail className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-semibold text-foreground mb-1">Email</h3>
                                    <Link to="mailto:k.m.mechanical24@gmail.com" className="text-muted-foreground hover:text-primary transition-colors">
                                        k.m.mechanical24@gmail.com
                                    </Link>
                                </div>
                            </div>

                            {}
                            <div className="flex items-start gap-4 group">
                                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/10 flex items-center justify-center transition-all duration-[120ms] ease-in-out group-hover:-translate-y-0.5">
                                    <MapPin className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-semibold text-foreground mb-1">Address</h3>
                                    <p className="text-muted-foreground"></p>
                                </div>
                            </div>

                            {}
                            <div className="flex items-start gap-4 group">
                                <div className="flex-shrink-0 size-12 rounded-lg bg-primary/10 flex items-center justify-center transition-all duration-[120ms] ease-in-out group-hover:-translate-y-0.5">
                                    <Clock className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-semibold text-foreground mb-1">Business Hours</h3>
                                    <p className="text-muted-foreground">
                                        Mon-Fri: 7am-6pm<br />
                                        Sat: 8am-2pm
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-foreground font-medium mb-4">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="w-full">
                    Send Another Message
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <FormField key="name-field" control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Name must not exceed 50 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                            <Input placeholder="Your Name" {...field} className="transition-all duration-[120ms] focus:translate-y-[-2px] focus:shadow-md" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField key="email-field" control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                            <Input type="email" placeholder="you@example.com" {...field} className="transition-all duration-[120ms] focus:translate-y-[-2px] focus:shadow-md" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField key="phone-field" control={control} name="phone" rules={{
      required: "Phone number is required",
      pattern: {
        value: /^[\d\s()+-]+$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                            <Input type="tel" placeholder="(412) 555-7890" {...field} className="transition-all duration-[120ms] focus:translate-y-[-2px] focus:shadow-md" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField key="message-field" control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      },
      maxLength: {
        value: 500,
        message: "Message must not exceed 500 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Tell us about your HVAC needs..." {...field} className="min-h-32 transition-all duration-[120ms] focus:translate-y-[-2px] focus:shadow-md" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[120ms] hover:-translate-y-0.5 hover:shadow-md">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-4" />
                        Sending...
                    </> : 'Send Message'}
            </Button>
        </Form>;
}